import * as Dialog from '@radix-ui/react-dialog';
import { X, ZoomIn } from 'lucide-react';
import * as React from 'react';
import { ImageWithLoader } from '../ImageWithLoader';
import './enlargeable-image.scss';

/**
 * TRIL-620: a post image the reader can open full-size.
 *
 * ONE component for every reader surface. The four web readers — the live post
 * (`UserPost.component`), version history + reply detail (`PostBlocks`), and the two draft
 * previews — each rendered a near-identical `<ImageWithLoader>` block; they differed only in alt
 * text, which the drafts set to "Draft Image" and which `fallbackAlt` preserves. Adding the
 * affordance to one of them is how this codebase has repeatedly shipped half a fix (web/mobile,
 * post/reply, the two draft helpers), so the call sites were consolidated onto this instead.
 *
 * WEB GETS AN EXPLICIT AFFORDANCE, mobile does not. A desktop reader has no reason to guess an
 * inline image is clickable; on a phone, tap-to-enlarge is an established convention and an
 * overlay icon costs screen space on the platform with least of it. Same capability, different
 * disclosure — a deliberate asymmetry, not an oversight.
 *
 * NO SESSION DEPENDENCY: no context, no hooks beyond local state, which is why the test renders
 * it bare. Note the stated reason in the first draft of this comment was WRONG — the TRIL-460
 * anonymous summary path (PostSummaryCard) renders no blocks at all, so a signed-out reader never
 * reaches this component today. The property is worth keeping because every surface that DOES
 * render it is inside an authed shell for unrelated reasons, not because anonymous readers depend
 * on it.
 */
export function EnlargeableImage(props: {
  src: string;
  /** The author's caption, when the block carries one. Shown beneath the enlarged image. */
  caption?: string;
  /**
   * Alt text when the block carries no caption. The draft previews say "Draft image" so a
   * screen-reader user editing a draft is not told they are looking at a published post.
   */
  fallbackAlt?: string;
  className?: string;
}) {
  const [open, setOpen] = React.useState(false);
  // The affordance exists only when there is something to enlarge.
  //
  // ImageWithLoader swaps in a "Failed to load image" placeholder on error, but the trigger is a
  // SIBLING and was unaffected: after a failure it stayed focusable and opened a black scrim over
  // a dead src. Gating on 'loaded' also removes a subtler one — pressing during load opened a
  // lightbox whose second <img> restarted the download from zero, which on a slow connection is
  // the difference between an image arriving and not.
  const [status, setStatus] = React.useState<'loading' | 'loaded' | 'error'>(
    'loading',
  );

  // Trimmed ONCE, here. A caption of "   " is not a caption: left untrimmed it produces an empty
  // <p>, an alt attribute of whitespace, and an accessible name ending in a dangling colon.
  const caption = props.caption?.trim() || '';

  // Every post image has shipped with the literal alt "Post Image" — true of all of them, so it
  // distinguishes none of them. A caption is the only real description the block carries.
  const describedAlt = caption || props.fallbackAlt || 'Post image';

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      {/* The wrapper is the hover target for the loupe. It is NOT the trigger: making the whole
          image a button would swallow text selection and give the image an interactive role it
          does not need for readers who will never enlarge it. */}
      <div className={`enlargeable-image ${props.className ?? ''}`}>
        <ImageWithLoader
          src={props.src}
          alt={describedAlt}
          style={{ maxWidth: '100%', height: 'auto' }}
          onStatusChange={setStatus}
        />
        {/* `|| open` keeps the trigger mounted while the dialog is: if the image errors mid-view,
            removing it would leave Radix returning focus to a node that no longer exists, which
            drops the keyboard user at <body> — on a long post, losing their place entirely. */}
        {status === 'loaded' || open ? (
          <Dialog.Trigger asChild>
            {/* A real <button>: keyboard reachable, not a hover-only div. Hover REVEALS it, but
              :focus-visible does too — a hover-only affordance is precisely the pattern the
              TRIL-373 accessibility gate exists to catch, and a keyboard user would otherwise
              have no way to reach it. */}
            <button
              type="button"
              className="enlargeable-image__trigger"
              aria-label={
                caption ? `Enlarge image: ${caption}` : 'Enlarge image'
              }
            >
              <ZoomIn size={18} aria-hidden="true" />
            </button>
          </Dialog.Trigger>
        ) : null}
      </div>

      <Dialog.Portal>
        {/* Radix gives us Esc, backdrop dismissal via onOpenChange, focus INTO the dialog on open
            and back to the trigger on close. Not hand-rolled — that is four separate things to
            get wrong, and the ticket's acceptance criteria name three of them. */}
        <Dialog.Overlay
          className="enlargeable-image__overlay"
          data-testid="enlargeable-overlay"
        />
        <Dialog.Content className="enlargeable-image__content">
          <img
            src={props.src}
            alt={describedAlt}
            className="enlargeable-image__full"
          />
          {/* THE VISIBLE CAPTION IS THE DIALOG TITLE. Radix requires an accessible name — without
              a Title it warns and screen readers announce an unnamed dialog — and rendering a
              hidden title ALONGSIDE a visible caption says the same sentence twice: once as the
              dialog's name, once as content. `asChild` makes the one element serve both. */}
          {caption ? (
            <Dialog.Title asChild>
              <p className="enlargeable-image__caption">{caption}</p>
            </Dialog.Title>
          ) : (
            <Dialog.Title className="enlargeable-image__sr-only">
              Enlarged image
            </Dialog.Title>
          )}
          <Dialog.Close asChild>
            <button
              type="button"
              className="enlargeable-image__close"
              aria-label="Close enlarged image"
            >
              <X size={20} aria-hidden="true" />
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export default EnlargeableImage;
